import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { getSocket } from "../../../lib/socket.js";
import { useAuth } from "../../../lib/auth-context.js";

interface ConversationListItem {
  id: string;
  status: string;
  lastMessageAt: string | null;
  unreadCount: number;
  hasUnread: boolean;
  assignedToUserId: string | null;
  assignedAt: string | null;
  contact: {
    id: string;
    igUsername: string | null;
    name: string | null;
    profilePicUrl: string | null;
  };
  igAccount: {
    id: string;
    igUsername: string;
    profilePicUrl: string | null;
  };
}

type StatusFilter = "open" | "resolved" | "blocked" | "all";
type AssignedFilter = "any" | "me" | "unassigned";

interface IgAccount {
  id: string;
  igUsername: string;
  profilePicUrl: string | null;
  status: string;
}

interface SearchHit {
  conversationId: string;
  contact: {
    id: string;
    igUsername: string | null;
    name: string | null;
    profilePicUrl: string | null;
  };
  snippet: string;
  rank: number;
  kind: "message" | "note" | "contact";
  ts: string | null;
}

const ALL_ACCOUNTS = "__all__";

export const Route = createFileRoute("/_app/inbox/")({
  component: InboxPage,
});

function InboxPage() {
  const { workspace, user, activeIgAccount } = useAuth();
  const currentUserId = user?.id ?? null;
  const [convs, setConvs] = useState<ConversationListItem[]>([]);
  const [accounts, setAccounts] = useState<IgAccount[]>([]);
  const [filter, setFilter] = useState<string>(() => ALL_ACCOUNTS);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [assignedFilter, setAssignedFilter] = useState<AssignedFilter>("any");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchHits, setSearchHits] = useState<SearchHit[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [onboarding, setOnboarding] = useState<{
    hasIgAccount: boolean;
    hasPublishedFlow: boolean;
    complete: boolean;
  } | null>(null);
  const [notifPerm, setNotifPerm] = useState<NotificationPermission | "unsupported">(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported",
  );
  const [searching, setSearching] = useState(false);

  // Only `active` accounts are filterable. Disconnected ones may still
  // own historical conversations — those still surface under "All".
  const activeAccounts = useMemo(
    () => accounts.filter((a) => a.status === "active"),
    [accounts],
  );

  // Reauth banner: any account in reauth_required means the user has
  // to redo OAuth before sends will work for that account.
  const reauthAccounts = useMemo(
    () => accounts.filter((a) => a.status === "reauth_required"),
    [accounts],
  );

  // Sync the filter to the active IG account when it changes.
  useEffect(() => {
    if (activeIgAccount) setFilter(activeIgAccount.id);
  }, [activeIgAccount?.id]);

  useEffect(() => {
    if (!activeIgAccount) return;
    const initialFilter = activeIgAccount.id;
    void Promise.all([loadAccounts(), loadConversations(initialFilter)]).then(
      () => setLoading(false),
    );
    // Onboarding state — non-fatal if it fails. Re-checked on every
    // workspace switch so a fresh workspace shows the banner again.
    api
      .get<{
        hasIgAccount: boolean;
        hasPublishedFlow: boolean;
        complete: boolean;
      }>("/me/onboarding")
      .then((r) => setOnboarding(r.data))
      .catch(() => setOnboarding(null));
  }, [workspace?.id, activeIgAccount?.id]);

  useEffect(() => {
    void loadConversations(filter);
  }, [filter, statusFilter, assignedFilter]);

  // Debounce search input — wait 300ms after the user stops typing
  // before hitting the API. Empty input clears any prior results.
  useEffect(() => {
    const term = searchQ.trim();
    if (term.length === 0) {
      setSearchHits(null);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      void api
        .get<{ results: SearchHit[] }>("/inbox/search", {
          params: { q: term, limit: 30 },
        })
        .then((r) => setSearchHits(r.data.results))
        .catch(() => setSearchHits([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  // Subscribe to realtime inbox events. The realtime server scopes
  // events to the connecting user's workspace via the session cookie,
  // so we don't need to filter by workspace client-side. On any
  // message-in/out event we just re-pull the conversations list with
  // the current filter — cheap (≤30 rows) and avoids tricky list
  // merging.
  useEffect(() => {
    const sock = getSocket();
    const onEvent = () => void loadConversations(filter);
    // Browser notifications: only fire on inbound (a customer message),
    // only when the tab is hidden, only if the user has granted
    // permission. The conversation list re-render is handled separately
    // — this is purely the OS-level pop.
    const onInbound = (payload: {
      conversationId?: string;
      contact?: { igUsername?: string | null; name?: string | null };
      preview?: string;
    }) => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") return;
      if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
      const who =
        payload?.contact?.name ?? payload?.contact?.igUsername ?? "New message";
      const body = payload?.preview ?? "Tap to open the conversation.";
      try {
        const n = new Notification(who, { body, tag: payload?.conversationId });
        n.onclick = () => {
          window.focus();
          if (payload?.conversationId) {
            window.location.assign(`/inbox/${payload.conversationId}`);
          }
          n.close();
        };
      } catch {
        // Some browsers throw on certain Notification options; swallow.
      }
    };
    sock.on("conversation.message_received", onEvent);
    sock.on("conversation.message_received", onInbound);
    sock.on("conversation.message_sent", onEvent);
    return () => {
      sock.off("conversation.message_received", onEvent);
      sock.off("conversation.message_received", onInbound);
      sock.off("conversation.message_sent", onEvent);
    };
  }, [filter]);

  async function loadAccounts() {
    try {
      const res = await api.get<{ accounts: IgAccount[] }>("/ig/accounts");
      setAccounts(res.data.accounts);
    } catch {
      // Non-fatal — switcher just won't render. Inbox query still works.
    }
  }

  async function loadConversations(accountFilter: string) {
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (accountFilter !== ALL_ACCOUNTS) params.ig_account_id = accountFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (assignedFilter !== "any") params.assigned_to = assignedFilter;
      const res = await api.get<{ conversations: ConversationListItem[] }>(
        "/inbox/conversations",
        { params },
      );
      setConvs(res.data.conversations);
    } catch {
      setError("Failed to load inbox.");
    }
  }

  async function load() {
    setLoading(true);
    await Promise.all([loadAccounts(), loadConversations(filter)]);
    setLoading(false);
  }

  function toggleSelected(id: string) {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function bulkAction(action: "mark_read" | "resolve" | "reopen") {
    if (selectedIds.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      await api.post("/inbox/conversations/bulk", {
        conversationIds: Array.from(selectedIds),
        action,
      });
      setSelectedIds(new Set());
      await loadConversations(filter);
    } catch {
      setError("Bulk action failed.");
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <main style={{ maxWidth: 920, margin: "0 auto", padding: "16px 24px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
        <h1 style={{ fontSize: 32, fontWeight: 900, letterSpacing: "-0.02em", margin: 0 }}>
          Inbox
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {activeAccounts.length > 1 && (
            <select
              className="brut-input"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: "auto", paddingRight: 32 }}
            >
              <option value={ALL_ACCOUNTS}>All accounts</option>
              {activeAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  @{a.igUsername}
                </option>
              ))}
            </select>
          )}
          <button className="brut-button is-secondary" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </div>

      {notifPerm === "default" && (
        <div
          className="brut-card"
          style={{
            background: "#e6f4ff",
            marginBottom: 16,
            padding: 12,
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 14 }}>
            🔔 Get a desktop notification when a customer DMs you while
            this tab is in the background.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="brut-button"
              style={{ fontSize: 13, padding: "6px 12px" }}
              onClick={() => {
                void Notification.requestPermission().then((p) =>
                  setNotifPerm(p),
                );
              }}
            >
              Enable notifications
            </button>
            <button
              type="button"
              className="brut-button is-secondary"
              style={{ fontSize: 13, padding: "6px 12px" }}
              onClick={() => setNotifPerm("denied")}
              title="Hide this prompt — you can re-enable from your browser's site settings later"
            >
              Not now
            </button>
          </div>
        </div>
      )}

      {onboarding && !onboarding.complete && (
        <div
          className="brut-card"
          style={{
            background: "#fff8d8",
            marginBottom: 16,
            padding: 16,
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>
            👋 Welcome to OyeReply — let's get you set up
          </div>
          <ol
            style={{
              margin: 0,
              paddingLeft: 22,
              display: "grid",
              gap: 6,
              fontSize: 14,
            }}
          >
            <li
              style={{
                opacity: onboarding.hasIgAccount ? 0.5 : 1,
                textDecoration: onboarding.hasIgAccount ? "line-through" : undefined,
              }}
            >
              {onboarding.hasIgAccount ? "✅" : "1."} Connect an Instagram
              account.{" "}
              {!onboarding.hasIgAccount && (
                <Link to="/settings/instagram" style={{ fontWeight: 700 }}>
                  Connect →
                </Link>
              )}
            </li>
            <li
              style={{
                opacity: onboarding.hasPublishedFlow ? 0.5 : 1,
                textDecoration: onboarding.hasPublishedFlow ? "line-through" : undefined,
              }}
            >
              {onboarding.hasPublishedFlow ? "✅" : "2."} Publish your first
              auto-reply flow.{" "}
              {!onboarding.hasPublishedFlow && (
                <Link to="/flows" style={{ fontWeight: 700 }}>
                  Browse templates →
                </Link>
              )}
            </li>
          </ol>
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {(["open", "all", "resolved", "blocked"] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={statusFilter === s ? "brut-button" : "brut-button is-secondary"}
            style={{ fontSize: 12, padding: "4px 10px", textTransform: "capitalize" }}
          >
            {s}
          </button>
        ))}
        <span style={{ width: 1, height: 18, background: "var(--ink)", margin: "0 6px" }} />
        {(["any", "me", "unassigned"] as const).map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => setAssignedFilter(a)}
            className={assignedFilter === a ? "brut-button" : "brut-button is-secondary"}
            style={{ fontSize: 12, padding: "4px 10px" }}
          >
            {a === "me" ? "Mine" : a === "unassigned" ? "Unassigned" : "All assignees"}
          </button>
        ))}
      </div>

      <input
        type="search"
        className="brut-input"
        placeholder="Search messages, notes, contacts…"
        value={searchQ}
        onChange={(e) => setSearchQ(e.target.value)}
        style={{ width: "100%", marginBottom: 16 }}
        maxLength={200}
      />

      {searchHits !== null && (
        <section style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 8,
            }}
          >
            <h2 style={{ fontSize: 18, margin: 0 }}>
              Search results {searching && <span style={{ color: "var(--muted)" }}>· …</span>}
            </h2>
            <button
              type="button"
              className="brut-link-btn"
              onClick={() => setSearchQ("")}
              style={{ fontSize: 13 }}
            >
              Clear
            </button>
          </div>
          {searchHits.length === 0 ? (
            <div className="brut-card">
              <p style={{ margin: 0, color: "var(--muted)" }}>
                No matches for "{searchQ}".
              </p>
            </div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
              {searchHits.map((h, i) => (
                <li key={`${h.conversationId}-${h.kind}-${i}`}>
                  <Link
                    to="/inbox/$conversationId"
                    params={{ conversationId: h.conversationId }}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <div className="brut-card" style={{ padding: 14, display: "flex", gap: 12 }}>
                      <Avatar src={h.contact.profilePicUrl} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 4 }}>
                          <strong>{displayName(h.contact)}</strong>
                          <span style={{ fontSize: 11, color: "var(--muted)" }}>
                            {h.kind === "note" ? "📝 note" : h.kind === "contact" ? "contact" : "message"}
                            {h.ts && ` · ${formatWhen(h.ts)}`}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: "var(--muted)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {h.snippet}
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {error && <div className="brut-error">{error}</div>}

      {reauthAccounts.length > 0 && (
        <div
          className="brut-card"
          style={{
            background: "#fff8d8",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Action needed: reconnect Instagram
            </div>
            <div style={{ fontSize: 13 }}>
              {reauthAccounts.length === 1 && reauthAccounts[0]
                ? `@${reauthAccounts[0].igUsername} needs to be reconnected before sends work.`
                : `${reauthAccounts.length} accounts need to be reconnected before sends work.`}
            </div>
          </div>
          <Link to="/settings/instagram" className="brut-button">
            Go to Settings
          </Link>
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : convs.length === 0 ? (
        <div className="brut-card">
          <p style={{ margin: 0 }}>
            No conversations yet. When someone DMs your connected Instagram account,
            it'll show up here.
          </p>
        </div>
      ) : (
        <>
          {selectedIds.size > 0 && (
            <div
              className="brut-card"
              style={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: "var(--accent)",
                padding: "10px 14px",
                marginBottom: 12,
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontWeight: 700, marginRight: 8 }}>
                {selectedIds.size} selected
              </span>
              <button
                type="button"
                disabled={bulkBusy}
                className="brut-button"
                style={{ fontSize: 13, padding: "6px 10px" }}
                onClick={() => void bulkAction("mark_read")}
              >
                Mark read
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                className="brut-button"
                style={{ fontSize: 13, padding: "6px 10px" }}
                onClick={() => void bulkAction("resolve")}
              >
                Resolve
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                className="brut-button is-secondary"
                style={{ fontSize: 13, padding: "6px 10px" }}
                onClick={() => void bulkAction("reopen")}
              >
                Reopen
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                className="brut-button is-secondary"
                style={{ fontSize: 13, padding: "6px 10px", marginLeft: "auto" }}
                onClick={() => setSelectedIds(new Set())}
              >
                Clear
              </button>
            </div>
          )}
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
          {convs.map((c) => (
            <li key={c.id}>
              <div
                className="brut-card"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: 20,
                  background: c.unreadCount > 0 || c.hasUnread ? "#fff8d8" : undefined,
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(c.id)}
                  onChange={() => toggleSelected(c.id)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Select conversation with ${displayName(c.contact)}`}
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                />
              <Link
                to="/inbox/$conversationId"
                params={{ conversationId: c.id }}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Avatar src={c.contact.profilePicUrl} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    {displayName(c.contact)}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: "var(--muted)",
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>via @{c.igAccount.igUsername}</span>
                    {c.lastMessageAt && <span>· {formatWhen(c.lastMessageAt)}</span>}
                    {c.assignedToUserId && (
                      <span
                        style={{
                          fontSize: 11,
                          padding: "1px 6px",
                          background: "var(--accent)",
                          border: "var(--border)",
                          fontWeight: 700,
                        }}
                        title="Assigned"
                      >
                        {c.assignedToUserId === currentUserId ? "👤 you" : "👥 assigned"}
                      </span>
                    )}
                    {c.status === "resolved" && (
                      <span style={{ fontSize: 11, color: "#666" }}>· resolved</span>
                    )}
                  </div>
                </div>
              </Link>
              {c.unreadCount > 0 && (
                <span
                  style={{
                    background: "var(--ink)",
                    color: "var(--paper)",
                    padding: "4px 10px",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {c.unreadCount}
                </span>
              )}
              </div>
            </li>
          ))}
        </ul>
        </>
      )}
    </main>
  );
}

function Avatar({ src }: { src: string | null }) {
  return src ? (
    <img
      src={src}
      alt=""
      width={48}
      height={48}
      style={{ border: "var(--border)" }}
    />
  ) : (
    <div
      style={{
        width: 48,
        height: 48,
        border: "var(--border)",
        background: "var(--accent)",
      }}
    />
  );
}

function displayName(contact: { id: string; name: string | null; igUsername: string | null }): string {
  if (contact.name) return contact.name;
  if (contact.igUsername) return `@${contact.igUsername}`;
  // Enrichment hasn't run yet (or failed — common for private accounts).
  // Show "Instagram user" plus a short stable suffix so the row stays
  // distinguishable in a list of multiple unenriched conversations.
  return `Instagram user · ${contact.id.slice(0, 6)}`;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const min = Math.floor((now - d.getTime()) / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return d.toLocaleDateString();
}
